import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import {
  Client,
  Company,
  ExtractionSummary
} from "@/jenesys-types/jenesys-entities/config";

export interface ClientDashboardState {
  activeCompanyId: string;
  activeClientId?: string;
  activeCompany?: Company;
  activeClient?: Client;
  dashboard: ExtractionSummary;
  totalDashboard: ExtractionSummary;
  isDashboardLoading: boolean;
  isTotalDashboardLoading: boolean;
  dashboardError?: AppError;
}

interface LoadClientDashboardPayload {
  clientId?: Id;
  dateRange?: any;
}

export type LoadClientDashboardAction =
  PayloadAction<LoadClientDashboardPayload>;

interface ClientDashboardFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type ClientDashboardFailureAction =
  PayloadAction<ClientDashboardFailurePayload>;

interface ClientDashboardSuccessPayload {
  dashboard: ExtractionSummary;
}
export type ClientDashboardSuccessAction =
  PayloadAction<ClientDashboardSuccessPayload>;

const name = "state/features/ClientDashboard";

export const initialState: ClientDashboardState = {
  activeCompanyId: "",
  activeClientId: undefined,
  activeCompany: undefined,
  activeClient: undefined,
  isDashboardLoading: false,
  isTotalDashboardLoading: false,
  dashboardError: undefined,
  dashboard: {} as ExtractionSummary,
  totalDashboard: {} as ExtractionSummary
};

const ClientDashboardSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadClientDashboard: {
      reducer: (
        state: ClientDashboardState,
        _action: LoadClientDashboardAction
      ) => {
        state.isDashboardLoading = true;
      },
      prepare: (clientId: Id, dateRange: any) => ({
        payload: { clientId, dateRange }
      })
    },
    clientDashboardFailure: {
      reducer: (
        state: ClientDashboardState,
        action: ClientDashboardFailureAction
      ) => {
        state.dashboardError = action.payload.error;
        state.isDashboardLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    clientDashboardSuccess: {
      reducer: (
        state: ClientDashboardState,
        action: ClientDashboardSuccessAction
      ) => {
        state.isDashboardLoading = false;
        state.dashboard = action.payload.dashboard;
      },
      prepare: (dashboard: ExtractionSummary) => ({
        payload: { dashboard }
      })
    },
    loadClientTotalDashboard: {
      reducer: (
        state: ClientDashboardState,
        _action: LoadClientDashboardAction
      ) => {
        state.isTotalDashboardLoading = true;
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    clientTotalDashboardFailure: {
      reducer: (
        state: ClientDashboardState,
        action: ClientDashboardFailureAction
      ) => {
        state.dashboardError = action.payload.error;
        state.isTotalDashboardLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    clientTotalDashboardSuccess: {
      reducer: (
        state: ClientDashboardState,
        action: ClientDashboardSuccessAction
      ) => {
        state.totalDashboard = action.payload.dashboard;
        state.isTotalDashboardLoading = false;
      },
      prepare: (dashboard: ExtractionSummary) => ({
        payload: { dashboard }
      })
    }
  }
});

export const {
  clientDashboardSuccess,
  clientDashboardFailure,
  loadClientDashboard,
  clientTotalDashboardSuccess,
  clientTotalDashboardFailure,
  loadClientTotalDashboard
} = ClientDashboardSlice.actions;

// Selectors
export const selectClientDashboardState = (state: {
  features: { ClientDashboard: ClientDashboardState };
}): ClientDashboardState => state.features.ClientDashboard;

export const selectDashboard = createSelector(
  selectClientDashboardState,
  (state) => state.dashboard
);
export const selectIsDashboardLoading = createSelector(
  selectClientDashboardState,
  (state) => state.isDashboardLoading
);

export default ClientDashboardSlice.reducer;
